// ============================================
// IntelliGrow - Constants & Configuration
// ============================================

import type { NavItem, TechItem, ChartDataPoint } from '@/src/types';

// Brand
export const BRAND = {
  name: 'IntelliGrow',
  shortName: 'IG',
  tagline: 'AI Agency',
  year: 2024,
} as const;

// External Links
export const CALENDLY_URL = 'https://calendly.com/intelligroow-nvmb/30min';
export const GUESTLY_URL = 'https://app.intelligrow.shop';

// Navigation
export const NAV_ITEMS: NavItem[] = [
  {
    label: 'Servicios',
    href: '/servicios',
    children: [
      { label: 'Consultoría AI', href: '/servicios/consultoria' },
      { label: 'Chatbot CrewAI', href: '/servicios/chatbot-crewai' },
      { label: 'Chatbot n8n', href: '/servicios/chatbot-n8n' },
      { label: 'Automatización', href: '/servicios/automatizacion' },
      { label: 'Crew de Agentes', href: '/servicios/agents-crew' },
      { label: 'Agente Airbnb/Booking', href: '/servicios/airbnb-booking' },
    ],
  },
  {
    label: 'Empresa',
    href: '/empresa',
    children: [
      { label: 'Sobre Nosotros', href: '/empresa/sobre-nosotros' },
      { label: 'Blog', href: '/empresa/blog' },
      { label: 'Contacto', href: '/empresa/contacto' },
    ],
  },
  { label: 'Precios', href: '/#precios' },
  {
    label: 'Legal',
    href: '/legal',
    children: [
      { label: 'Privacidad', href: '/privacidad' },
      { label: 'Términos', href: '/terminos' },
    ],
  },
];

// Footer Links
export const FOOTER_SERVICES = [
  'Consultoría AI',
  'Desarrollo de Chatbots',
  'Automatización',
  'Crew de Agentes',
];

export const FOOTER_COMPANY = ['Sobre Nosotros', 'Blog', 'Contacto'];

// Tech Stack
export const TECH_STACK: TechItem[] = [
  { name: 'Python', icon: '🐍' },
  { name: 'OpenAI', icon: '🤖' },
  { name: 'AWS', icon: '☁️' },
  { name: 'Supabase', icon: '⚡' },
  { name: 'Vercel', icon: '▲' },
  { name: 'Pinecone', icon: '🌲' },
  { name: 'LangChain', icon: '🦜' },
  { name: 'Stripe', icon: '💳' },
];

// Technologies we work with
export const TECH_CAROUSEL = [
  'Python',
  'SQL',
  'PostgreSQL',
  'Machine Learning',
  'CrewAI',
  'Deep Learning',
  'Reinforcement Learning',
  'Robotics',
];

// ROI Chart Data
export const ROI_CHART_DATA: ChartDataPoint[] = [
  { name: 'Jan', value: 10 },
  { name: 'Feb', value: 15 },
  { name: 'Mar', value: 12 },
  { name: 'Apr', value: 25 },
  { name: 'May', value: 45 },
  { name: 'Jun', value: 70 },
  { name: 'Jul', value: 95 },
];

// Chat Configuration
export const CHAT_CONFIG = {
  maxMessages: 3,
  modelName: 'gemini-2.5-flash',
} as const;

// Agency Chat System Prompt
export const AGENCY_CHAT_PROMPT = `
Eres el Asistente Virtual Oficial de IntelliGrow, una agencia de IA premium.
Tu objetivo es ayudar a potenciales clientes a entender nuestros servicios y agendar reuniones.

INFORMACIÓN DE LA AGENCIA:
1. SERVICIOS:
   - Chatbots Inteligentes (NLP, soporte 24/7).
   - Automatización Total (RPA, CRMs, Email).
   - AI Dashboards (Analítica predictiva).
   - Agentes de Voz para Web.
   - Facturación Autónoma.

2. MODELOS DE PRECIOS/TECNOLOGÍA:
   - Estándar (n8n): Automatización visual, rápida, ideal para conectar apps y flujos lineales. Coste menor.
   - Enterprise (CrewAI / Python): Agentes autónomos complejos, código a medida, lógica de razonamiento avanzada. Coste mayor por la complejidad técnica.

3. EQUIPO:
   - Nos diferenciamos por ser Científicos de Datos, no solo implementadores. 
   - Enfoque pragmático: Resultados medibles, ROI rápido.

DIRECTRICES DE RESPUESTA:
- Sé profesional, conciso y con un tono "tech enterprise".
- Si preguntan precios exactos, di que depende de la complejidad y sugieres "Hablar con un Experto".
- Usa formato Markdown (negritas) para resaltar puntos clave.
- Idioma: Español.
`;

// RAG Demo System Prompt
export const RAG_DEMO_PROMPT = `
Eres un asistente de IA amable y servicial para un apartamento vacacional llamado "Casa Sunset".
Toda tu información proviene del documento "Guía_Casa_Vacacional.pdf".

Datos clave del documento:
1. WIFI: Red "Sunset_Guest", Contraseña "HolaVerano2025".
2. PISCINA: Abierta de 8:00 AM a 10:00 PM. No se permite vidrio ni comida en la zona de agua.
3. CHECK-OUT: A las 11:00 AM. Dejar las llaves en el buzón rojo de la entrada.
4. BASURA: Contenedores verdes en la esquina de la calle principal.
5. EMERGENCIAS: Hospital a 5 min. Extintor en la cocina bajo el fregadero.

Si te preguntan algo que no está aquí, di amablemente que no tienes esa información en el documento proporcionado.
Sé conciso y usa formato Markdown (negritas) para datos importantes.
`;

// Animation Presets
export const ANIMATIONS = {
  fadeInUp: {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 },
  },
  fadeInLeft: {
    initial: { opacity: 0, x: -20 },
    animate: { opacity: 1, x: 0 },
    transition: { duration: 0.6 },
  },
  fadeInRight: {
    initial: { opacity: 0, x: 20 },
    animate: { opacity: 1, x: 0 },
    transition: { duration: 0.6 },
  },
  scaleIn: {
    initial: { opacity: 0, scale: 0.9 },
    animate: { opacity: 1, scale: 1 },
    transition: { duration: 0.4 },
  },
  springConfig: {
    damping: 25,
    stiffness: 120,
    mass: 0.5,
  },
} as const;

// Breakpoints (matching Tailwind)
export const BREAKPOINTS = {
  sm: 640,
  md: 768,
  lg: 1024,
  xl: 1280,
  '2xl': 1536,
} as const;

// Social Links
export const SOCIAL_LINKS = {
  twitter: 'https://x.com/intelligroow',
  linkedin: 'https://www.linkedin.com/in/elena-guti%C3%A9rrez-5a74b7330',
  tiktok: 'https://www.tiktok.com/@intelligrow',
  instagram: 'https://www.instagram.com/intelligroow',
} as const;

// Blog Posts
export const BLOG_POSTS = [
  {
    slug: 'que-es-crewai-agentes-autonomos',
    title: '¿Qué es CrewAI? Guía completa de agentes autónomos',
    excerpt:
      'Descubre cómo CrewAI permite crear equipos de agentes de IA que colaboran para resolver tareas complejas.',
    date: '2024-12-01',
    readTime: '8 min',
    category: 'Tecnología',
  },
  {
    slug: 'automatizacion-n8n-vs-codigo',
    title: 'Automatización: ¿n8n o código custom?',
    excerpt:
      'Analizamos cuándo usar herramientas low-code como n8n y cuándo invertir en desarrollo a medida.',
    date: '2024-11-15',
    readTime: '6 min',
    category: 'Automatización',
  },
  {
    slug: 'rag-retrieval-augmented-generation',
    title: 'RAG: Cómo hacer que tu IA conozca tu negocio',
    excerpt:
      'Retrieval Augmented Generation explicado: conecta tus documentos con modelos de lenguaje.',
    date: '2024-11-01',
    readTime: '10 min',
    category: 'IA',
  },
];
